import React, { FC, useEffect, useState, ReactElement, ChangeEvent } from 'react'
import { inject, observer } from 'mobx-react'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import * as yup from 'yup'
import { Formik } from 'formik'
import { Form, Alert } from 'react-bootstrap'
import API from '@aws-amplify/api'
import _ from 'lodash'
import { Select, MenuItem, withStyles, InputBase } from '@material-ui/core'
import { Prospects as ProspectsStore } from '../../../stores/prospects'
import { Search as SearchStore } from '../../../stores/search'
import Spinner from '../../Spinner'
import LoadingButton from '../../LoadingButton'
import { ToastTypeEnum } from '../../Dashboard/Toasts'
import { industriesPrefill } from '../../../utils/constants'
import './styles.scss'

const BootstrapInput = withStyles(theme => ({
  root: {
    width: '100%',
    maxWidth: '80%',
    marginTop: 0,
  },

  input: {
    borderRadius: 4,
    position: 'relative',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid #ced4da',
    fontSize: 16,
    padding: '10px 26px 10px 12px',
    transition: theme.transitions.create(['border-color', 'box-shadow']),
    color: '#495057',
    // Use the system font instead of the default Roboto font.
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:focus': {
      borderRadius: 4,
      borderColor: '#80bdff',
      boxShadow: '0 0 0 0.2rem rgba(0,123,255,.25)',
    },
  },
}))(InputBase)
interface Props extends RouteComponentProps {
  // eslint-disable-next-line react/require-default-props
  prospects?: ProspectsStore
  // eslint-disable-next-line react/require-default-props
  search?: SearchStore
  urlEmail: string
  onSave: () => void
}

const schema = yup.object({
  firstName: yup.string(),
  lastName: yup.string(),
  title: yup.string(),
  company: yup.string(),
  industry: yup.string(),
  linkedIn: yup
    .string()
    .url('LinkedIn must be a valid LinkedIn URL starting with https://www.linkedin.com/'),
  twitter: yup
    .string()
    .url('Twitter must be a valid Twitter URL starting with https://twitter.com/'),
  phone: yup.string(),
  email: yup.string().email('Must be a valid email'),
})

const EditForm: FC<Props> = ({ urlEmail, prospects, search, onSave }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [industryType, setIndustryType] = useState(prospects?.prospect?.industry)

  const editLead = async (values: {
    firstName: string
    lastName: string
    title: string
    company: string
    industry: string
    linkedIn: string
    twitter: string
    phone?: string
    email: string
  }): Promise<void> => {
    setIsLoading(true)

    const payload = {
      // need leadId to update prospect
      leadId: prospects?.prospect?.id,
      firstName: values.firstName,
      lastName: values.lastName,
      title: values.title,
      company: values.company,
      industry: industryType,
      linkedIn: values.linkedIn,
      twitter: values.twitter,
      phone: values.phone,
      email: values.email,
    }

    const myInit = { body: payload }

    try {
      await API.put('mav-outlook-api', `/leads/${prospects?.prospect?.email}`, myInit)

      // console.log(response)
      if (onSave) {
        onSave()
      }
      setIsLoading(false)

      const editedProspect = _.cloneDeep(prospects?.prospect)

      if (editedProspect) {
        editedProspect.name = `${values.firstName} ${values.lastName}`
        editedProspect.firstName = values.firstName
        editedProspect.lastName = values.lastName
        editedProspect.title = values.title
        editedProspect.company = values.company
        editedProspect.industry = industryType as string
        editedProspect.linkedIn = values.linkedIn
        editedProspect.twitter = values.twitter
        editedProspect.email = values.email

        if (editedProspect.phoneNumber && values.phone) {
          editedProspect.phoneNumber.number = values.phone
        }

        prospects?.setProspect(editedProspect)

        const data = {}
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const id: any = prospects?.prospect?.id
        data[id] = { ...payload }
        search?.updateProspectsData(data)
        search?.setCacheValid(false)
      }
    } catch (e) {
      console.error(e)

      setIsLoading(false)

      prospects?.pushToToasts({
        type: ToastTypeEnum.ERROR,
        message: 'Unable to update lead',
      })
    }
  }

  const onClientTypeChange = (
    event: ChangeEvent<{ name?: string; value: unknown }>, // eslint-disable-line @typescript-eslint/member-delimiter-style
  ): void => {
    setIndustryType(event.target.value as string)
  }

  useEffect(() => {
    if (prospects?.prospect?.email !== urlEmail) {
      prospects?.getLead(urlEmail)
    }
  }, [])

  if (prospects?.isLoading) {
    return <Spinner />
  }

  if (prospects?.prospect) {
    return (
      <div className="edit-container">
        <Formik
          validationSchema={schema}
          onSubmit={editLead}
          initialValues={{
            firstName: prospects.prospect.firstName,
            lastName: prospects.prospect.lastName,
            title: prospects.prospect.title,
            company: prospects.prospect.company,
            industry: prospects.prospect.industry,
            email: prospects.prospect.email,
            phone: prospects.prospect.phoneNumber?.number,
            linkedIn: prospects.prospect.linkedIn,
            twitter: prospects.prospect.twitter,
            instagram: prospects.prospect.instagram,
            facebook: prospects.prospect.facebook,
          }}
        >
          {({ handleSubmit, values, handleChange, touched, errors, submitCount }): ReactElement => (
            <Form noValidate onSubmit={handleSubmit} className="form">
              <Form.Row className="row">
                <Form.Group className="mb-3">
                  <Form.Group controlId="firstName">
                    <Form.Row>
                      <Form.Label>First Name</Form.Label>
                      <Form.Control
                        type="text"
                        autoFocus
                        required
                        value={values.firstName}
                        onChange={handleChange}
                        isValid={touched.firstName && !errors.firstName}
                        isInvalid={submitCount > 0 && !!errors.firstName}
                        placeholder="Samplefirstb"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.firstName}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="lastName">
                    <Form.Row>
                      <Form.Label>Last Name</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.lastName}
                        onChange={handleChange}
                        isValid={touched.lastName && !errors.lastName}
                        isInvalid={submitCount > 0 && !!errors.lastName}
                        placeholder="Samplelastb"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.lastName}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="title">
                    <Form.Row>
                      <Form.Label>Title</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.title}
                        onChange={handleChange}
                        isValid={touched.title && !errors.title}
                        isInvalid={submitCount > 0 && !!errors.title}
                        placeholder="Titlehere"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.title}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="company">
                    <Form.Row>
                      <Form.Label>Company</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.company}
                        onChange={handleChange}
                        isValid={touched.company && !errors.company}
                        isInvalid={submitCount > 0 && !!errors.company}
                        placeholder="Companyname"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.company}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="industry">
                    <Form.Row>
                      <Form.Label>Industry</Form.Label>
                      <Select
                        labelId="industry-select-label"
                        id="industry-select"
                        value={industryType}
                        onChange={onClientTypeChange}
                        input={<BootstrapInput />}
                      >
                        {industriesPrefill.map((industry: string) => (
                          <MenuItem value={industry} key={industry}>
                            {industry}
                          </MenuItem>
                        ))}
                      </Select>
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.industry}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="email">
                    <Form.Row>
                      <Form.Label>Email</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.email}
                        onChange={handleChange}
                        isValid={touched.email && !errors.email}
                        isInvalid={submitCount > 0 && !!errors.email}
                        placeholder="firstlastname@domain.com"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.email}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                </Form.Group>
                <Form.Group className="mb-3">
                  <Form.Group controlId="phone">
                    <Form.Row>
                      <Form.Label>Phone</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.phone}
                        onChange={handleChange}
                        isValid={touched.phone && !errors.phone}
                        isInvalid={submitCount > 0 && !!errors.phone}
                        placeholder="123.123.1234"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.phone}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="linkedIn">
                    <Form.Row>
                      <Form.Label>LinkedIn URL</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.linkedIn}
                        onChange={handleChange}
                        isValid={touched.linkedIn && !errors.linkedIn}
                        isInvalid={submitCount > 0 && !!errors.linkedIn}
                        placeholder="linkedin.com/firstnamelastname"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.linkedIn}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="twitter">
                    <Form.Row>
                      <Form.Label>Twitter URL</Form.Label>
                      <Form.Control
                        type="text"
                        required
                        value={values.twitter}
                        onChange={handleChange}
                        isValid={touched.twitter && !errors.twitter}
                        isInvalid={submitCount > 0 && !!errors.twitter}
                        placeholder="twitter.com/firstnamelastname"
                      />
                    </Form.Row>
                    <Form.Control.Feedback type="invalid">
                      <Alert variant="danger">{errors.twitter}</Alert>
                    </Form.Control.Feedback>
                  </Form.Group>
                  <Form.Group controlId="instagramUrl">
                    <Form.Row>
                      <Form.Label>Instagram Url</Form.Label>
                      <Form.Control
                        type="text"
                        autoFocus
                        required
                        value={values.instagram}
                        onChange={handleChange}
                        isValid={touched.instagram && !errors.instagram}
                        isInvalid={submitCount > 0 && !!errors.instagram}
                        placeholder="instagram.com/charliefliesbetter"
                      />
                      <Form.Control.Feedback type="invalid">
                        <Alert variant="danger">{errors.instagram}</Alert>
                      </Form.Control.Feedback>
                    </Form.Row>
                  </Form.Group>
                  <Form.Group controlId="facebookUrl">
                    <Form.Row>
                      <Form.Label>Facebook Url</Form.Label>
                      <Form.Control
                        type="text"
                        autoFocus
                        required
                        value={values.facebook}
                        onChange={handleChange}
                        isValid={touched.facebook && !errors.facebook}
                        isInvalid={submitCount > 0 && !!errors.facebook}
                        placeholder="facebook.com/charliefliesbetter"
                      />
                      <Form.Control.Feedback type="invalid">
                        <Alert variant="danger">{errors.facebook}</Alert>
                      </Form.Control.Feedback>
                    </Form.Row>
                  </Form.Group>
                </Form.Group>
              </Form.Row>
              <Form.Group controlId="actions">
                <LoadingButton variant="primary" block isLoading={isLoading} type="submit">
                  Update Contact
                </LoadingButton>
              </Form.Group>
            </Form>
          )}
        </Formik>
      </div>
    )
  }

  return null
}

export default inject('prospects', 'search')(withRouter(observer(EditForm)))
